import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { TILES_2048, INIT_2048_BOARD, moveBoard, isGameOver, hasWon } from '@/mocks/2048-data';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

export default function Game2048() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();
  const [board, setBoard] = useState<(number | null)[][]>(INIT_2048_BOARD());
  const [totalScore, setTotalScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [started, setStarted] = useState(false);

  const handleStart = () => {
    setBoard(INIT_2048_BOARD());
    setTotalScore(0);
    setGameOver(false);
    setWon(false);
    setShowGameOverModal(false);
    setStarted(true);
    startGame('2048');
  };

  const doMove = useCallback((dir: 'up' | 'down' | 'left' | 'right') => {
    if (gameOver || !started || gameStatus !== 'playing') return;
    const result = moveBoard(board, dir);
    if (result.moved) {
      setBoard(result.board);
      setTotalScore(s => {
        const ns = s + result.score;
        updateScore(ns);
        return ns;
      });

      if (hasWon(result.board) && !won) {
        setWon(true);
        unlockAchievement('anatomie_master');
      }
      if (isGameOver(result.board)) {
        setGameOver(true);
        setTimeout(() => {
          endGame(totalScore + result.score, gameTime, gameErrors);
          setShowGameOverModal(true);
        }, 500);
      }
    }
  }, [board, gameOver, started, gameStatus, won, totalScore, gameTime, gameErrors, updateScore, endGame, unlockAchievement]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); doMove('up'); }
      if (e.key === 'ArrowDown') { e.preventDefault(); doMove('down'); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); doMove('left'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); doMove('right'); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [doMove]);

  const handleReplay = () => handleStart();
  const handleQuit = () => {
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!started) {
    return (
      <GameWrapper gameName="2048 Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-grid-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">2048 Santé</h2>
            <p className="text-foreground-600 text-sm mb-4">Fusionne de la Cellule jusqu&apos;à l&apos;Immunité !</p>
            <div className="text-xs text-foreground-500 mb-4 space-y-1">
              {[2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048].map(v => {
                const t = TILES_2048[v];
                return (
                  <span key={v} className={`inline-block px-2 py-0.5 rounded m-0.5 ${t.color} ${t.textColor} text-xs font-medium`}>
                    {v}: {t.label}
                  </span>
                );
              })}
            </div>
            <button
              onClick={handleStart}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill mr-2"></i> Commencer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="2048 Santé" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {won && (
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Immunité atteinte ! Continue à jouer !
          </div>
        )}

        <div className="bg-background-200 rounded-2xl p-3 w-full max-w-sm">
          <div className="grid grid-cols-4 gap-2">
            {board.flatMap((row, r) =>
              row.map((cell, c) => {
                const tile = cell ? TILES_2048[cell] || TILES_2048[2] : null;
                return (
                  <div
                    key={`${r}-${c}`}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center transition-all duration-100 ${
                      tile ? tile.color : 'bg-background-100/50'
                    }`}
                  >
                    {tile && (
                      <>
                        <span className={`text-lg font-bold ${tile.textColor}`}>{tile.value}</span>
                        <span className={`text-[9px] font-medium ${tile.textColor} leading-tight`}>{tile.label}</span>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <p className="text-xs text-foreground-500 mt-4">Utilise les flèches du clavier</p>

        {/* Mobile buttons */}
        <div className="mt-3 grid grid-cols-3 gap-2 md:hidden">
          <div /><button onClick={() => doMove('up')} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center hover:bg-background-200 cursor-pointer"><i className="ri-arrow-up-line"></i></button><div />
          <button onClick={() => doMove('left')} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center hover:bg-background-200 cursor-pointer"><i className="ri-arrow-left-line"></i></button>
          <div className="w-12 h-12" />
          <button onClick={() => doMove('right')} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center hover:bg-background-200 cursor-pointer"><i className="ri-arrow-right-line"></i></button>
          <div /><button onClick={() => doMove('down')} className="w-12 h-12 bg-background-100 rounded-xl flex items-center justify-center hover:bg-background-200 cursor-pointer"><i className="ri-arrow-down-line"></i></button><div />
        </div>
      </div>

      {showGameOverModal && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="2048"
          message={won ? 'Immunité atteinte !' : 'Partie terminée'}
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}